import { useMemo } from "react";
import { createPortal } from "react-dom";
import dayjsTZ from "../../../utils/dayjs";
import "./AquamanPrintSheet.scss";

// A single A4 page to pin up in the office: who changes the water, which week.
// It renders into <body> through a portal so the print stylesheet can hide the
// rest of the app with one rule instead of naming every layout wrapper.
const END_OF_DAY_MS = 86_399_999;
// The sheet hangs on the wall for the whole run it covers, so it prints twenty
// weeks: two columns of ten, sized to stay readable from across the room. No
// week is marked as current, since that would be wrong days after printing.
const MAX_ROWS = 20;
// A headless Chrome render fits exactly thirteen single column rows on A4, so
// switching to two columns at thirteen leaves a row of slack for printers and
// browsers that lay the type out slightly larger.
const TWO_COLUMN_THRESHOLD = 12;

const BODY_CLASS = "aquamanPrinting";

// Kept next to the stylesheet that depends on the class name.
export function printAquamanSheet() {
  document.body.classList.add(BODY_CLASS);

  const cleanup = () => {
    document.body.classList.remove(BODY_CLASS);
    window.removeEventListener("afterprint", cleanup);
  };

  window.addEventListener("afterprint", cleanup);
  window.print();
  // Safari has historically not fired afterprint; this stops the class from
  // sticking and hiding the app.
  setTimeout(cleanup, 60_000);
}

const AquamanPrintSheet = ({ roster, weeks }) => {
  const rows = useMemo(() => {
    const now = Date.now();
    return [...(weeks || [])]
      .filter((w) => w.weekEnd + END_OF_DAY_MS >= now)
      .sort((a, b) => a.weekStart - b.weekStart)
      .slice(0, MAX_ROWS);
  }, [weeks]);

  if (!roster || rows.length === 0) return null;

  const twoColumns = rows.length > TWO_COLUMN_THRESHOLD;

  return createPortal(
    <div className="aquamanPrintRoot">
      <article className="aquamanPrintSheet">
        <header>
          <h1>AQUAMAN</h1>
          <p className="team">{roster.teamLabel || roster.team}</p>
          <p className="period">
            {dayjsTZ(rows[0].weekStart).format("DD/MM/YYYY")} –{" "}
            {dayjsTZ(rows[rows.length - 1].weekEnd).format("DD/MM/YYYY")}
          </p>
        </header>

        <ol
          className={`printWeeks ${twoColumns ? "twoCols" : ""}`}
          style={
            twoColumns
              ? {
                  gridTemplateRows: `repeat(${Math.ceil(
                    rows.length / 2
                  )}, auto)`,
                }
              : undefined
          }
        >
          {rows.map((week) => (
            <li key={week.weekStart}>
              <span className="dates">
                {dayjsTZ(week.weekStart).format("DD/MM")} –{" "}
                {dayjsTZ(week.weekEnd).format("DD/MM")}
              </span>
              <span className="who">
                {week.status === "skipped"
                  ? "pa shërbim"
                  : week.employeeName || "—"}
              </span>
            </li>
          ))}
        </ol>

        <footer>
          <span>Ndërrimi i ujit — një person për javë</span>
          <span>Printuar {dayjsTZ().format("DD/MM/YYYY")}</span>
        </footer>
      </article>
    </div>,
    document.body
  );
};

export default AquamanPrintSheet;
